import React from 'react';

type AnimatedNumberProps = {
  value: number;
  durationMs?: number;
  className?: string;
  formatter?: (n: number) => string | number;
};

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  durationMs = 350,
  className,
  formatter,
}) => {
  const [displayValue, setDisplayValue] = React.useState<number>(Number(value) || 0);
  const previousRef = React.useRef<number>(Number(value) || 0);

  React.useEffect(() => {
    const next = Number(value) || 0;
    if (previousRef.current === next) return;
    const start = previousRef.current;
    const delta = next - start;
    let raf = 0;
    const startTs = performance.now();
    const step = (ts: number) => {
      const t = Math.min(1, (ts - startTs) / durationMs);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad
      setDisplayValue(Math.round(start + delta * eased));
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        previousRef.current = next;
        setDisplayValue(next);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs]);

  const content = formatter ? formatter(displayValue) : String(displayValue);
  return (
    <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>{content}</span>
  );
};

type CrossfadeTextProps = { text: string; className?: string };
export const CrossfadeText: React.FC<CrossfadeTextProps> = ({ text, className }) => {
  const [display, setDisplay] = React.useState<string>(text);
  const [fade, setFade] = React.useState<'in' | 'out'>('in');

  React.useEffect(() => {
    if (text === display) return;
    setFade('out');
    const tm = setTimeout(() => {
      setDisplay(text);
      setFade('in');
    }, 150);
    return () => clearTimeout(tm);
  }, [text, display]);

  return (
    <span className={`inline-block transition-all duration-300 ${fade === 'in' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'} ${className || ''}`}>
      {display}
    </span>
  );
};

type WidthSlotProps = {
  show: boolean;
  width: number; // in px
  className?: string;
  children?: React.ReactNode;
};

export const AnimatedWidthSlot: React.FC<WidthSlotProps> = ({ show, width, className, children }) => {
  return (
    <span
      className={`inline-flex overflow-hidden transition-all duration-300 ${className || ''}`}
      style={{ width: show ? width : 0 }}
    >
      {show ? children : null}
    </span>
  );
};

export default AnimatedNumber;


